import React, { useState, useEffect, useMemo } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import Breadcrumb from "../../../../../components/Generic/Breadcrump";
import Locations from "../../../../../components/Generic/Locations";
import SubHeader from "../../../../../components/Generic/SubHeader";
import { NotificationToast } from "../../../../../components/Generic/Toast/NotificationToast";
import UITabMaterial, { PanelProps } from "../../../../../components/Generic/UITabMaterial";
import { PRIVATE_ROUTES } from "../../../../../constants";
import { AgentProvider } from "../../context/AgentContext";
import { useAppState } from "../../../../../context/StateContext";
import useActionAutomaticSequence from "../../../../../hooks/useActionAutomaticSequence";
import { useAgentHook } from "../../hooks/useAgentHook";
import { Agent } from "../../../../../models/agent";
import { AutomaticSequencing } from "../../../../../models/automatic.sequencing";
import { Error } from "../../../../../models/error";
import AgentService from "../../../../../services/agent.service";
import OnBoardingService from "../../../../../services/onBoarding.service";
import { checkError } from "../../../../../util/checkError";
import { CompanyProfile, AutomaticSequenceOptions, AgentStatus, UploadValueType } from "../../../../../util/enums";
import SkeletonAgent from "../../../../../pages/private/Shared/Network/SkeletonDetails";
import { AgentDetailsTab } from "../../components/AgentDetailsTab";
import useStyles from "./styles";
import { checkResourceCan, FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import { ResourceCanEnum, ResourceName } from "Util/enums/accessRule";
import Order from "../../../../../pages/private/Shared/Network/Agent/Maintenance/Order";
import { Pricing } from "../../../../../pages/private/Shared/Network/Agent/Maintenance/Pricing";
import Quote from "../../../../../pages/private/Shared/Network/Agent/Maintenance/Quote";
import T from "../../types/types";
import BadgeStatus from "Components/Generic/BadgeStatus";
import { isMoving } from "Util/validation";

enum TAB_SELECTED {
  DETAILS = "details",
  LOCATIONS = "locations",
  QUOTES = "quotes",
  ORDERS = "orders",
  PRICING = "pricing",
}

const AgentCreateOrEditPageContent = () => {
  const { agent, setAgent } = useAgentHook();

  const { state } = useLocation<T.LocationProps>();

  const [leftPanelSelected, setLeftPanelSelected] = useState<string>(TAB_SELECTED.DETAILS);

  const [viewOnly, setViewOnly] = useState<boolean>();
  const [sequence, setSequence] = useState<AutomaticSequencing>();
  const [createdAgent, setCreatedAgent] = useState<Agent>();
  const [responseError, setResponseError] = useState<Error>();

  const { currentUser, userPermissions } = useAppState();

  const classes = useStyles();

  const { t: translate } = useTranslation();

  const history = useHistory<any>();

  const { getAutomaticSequenceByType, checkForAutomaticSequence, increaseAutomaticSequence } =
    useActionAutomaticSequence();
  const [agentPermission, setAgentPermission] = useState<{
    edit: boolean;
    create: boolean;
  }>({
    edit: false,
    create: false,
  });

  getAutomaticSequenceByType(AutomaticSequenceOptions.AGENT, !agent.id, setSequence);

  const locationState = history.location?.state;

  const agentId: number | undefined =
    createdAgent && createdAgent?.id
      ? createdAgent?.id
      : locationState
      ? locationState.agentId
      : agent && agent.id
      ? agent.id
      : undefined;

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setAgentPermission({
        edit:
          checkResourceCan(userPermissions, ResourceName.AGENTS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_AGENTS_EDIT),
        create:
          checkResourceCan(userPermissions, ResourceName.AGENTS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_AGENTS_ADD_NEW),
      });
    }
  }, [userPermissions]);

  useEffect(() => {
    if (state) setViewOnly(state.viewOnly);
  }, [state]);

  const { isFetching } = useQuery(
    ["agent"],
    () =>
      AgentService.getById(Number(agentId), {
        include: JSON.stringify([
          { association: "associatedCompany" },
          { association: "contacts", order: [["id", "DESC"]] },
          { association: "timezone" },
          { association: "consultant" },
          {
            association: "locations",
            order: [["id", "DESC"]],
            include: [{ association: "contact" }],
          },
          { association: "warehouse" },
          {
            association: "services",
            include: [
              { association: "group" },
              { association: "service" },
              { association: "currency" },
            ],
          },
          { association: "logo" },
        ]),
      }),
    {
      enabled: !!agentId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          let mobilePhone = response.data.phone;

          if (mobilePhone && !mobilePhone.includes("+")) {
            if (mobilePhone.includes(": ")) {
              mobilePhone = `+${response.data.phone.split(": ")[1]}`;
            } else mobilePhone = `+${response.data.phone}`;
          }
          setAgent({ ...response.data, phone: mobilePhone });
        }
      },
    }
  );

  const { mutate: createAgent, isLoading: creatingAgent } = useMutation(
    ["agent.create"],
    (agent: Agent & { sendInviteAfterCreate?: boolean }) => AgentService.create(agent),
    {
      onSuccess: (response) => {
        setResponseError(undefined);
        if (sequence && agent?.code && sequence.id && checkForAutomaticSequence(agent.code)) {
          increaseAutomaticSequence({
            sequence,
            value: agent.code,
          });
        }

        if (response?.data?.id) {
          setCreatedAgent(response.data);
          
          if (response.data.associatedCompanyId) {
            OnBoardingService.updateDefault(
              response.data.associatedCompanyId,
              UploadValueType.UPLOAD_VALUE_SHIPEEZI_LIST,
            );
          }
        }
        goBack()
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) {
          checkError(error, translate("anErrorOccurredTryAgain"));
          setResponseError(error?.response?.data);
        }
      },
    }
  );

  const { mutate: updateAgent, isLoading: updatingAgent } = useMutation(
    ["agent.update"],
    ({ id, agent }: { id: number; agent: Agent }) => AgentService.update(id, agent),
    {
      onSuccess: () => {
        setResponseError(undefined);
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        goBack();
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) {
          checkError(error, translate("anErrorOccurredTryAgain"));
          setResponseError(error?.response?.data);
        }
      },
    }
  );

  const deleteAgent = (agent: Agent) => {
    if (agent && agent.id) {
      const deletedAgent = { ...agent, valid: false };

      updateAgent({ id: agent?.id, agent: deletedAgent });
    }
  };

  const goBack = () => {
    history.push(`${PRIVATE_ROUTES.NETWORK}?tab=agents`);
  };

  const detailsPanel: PanelProps = {
    key: "details",
    className: classnames(classes.uiTab, classes.tabDetails),
    menuItem: translate("details"),
    loading: createdAgent ? false : isFetching,
    skeleton: () => <SkeletonAgent />,
    render: () => {
      if (!isShowAction(currentUser, agentPermission.create)) return null;

      return (
        <AgentDetailsTab
          goBack={goBack}
          viewOnly={viewOnly}
          onDelete={() => (agent?.id ? deleteAgent(agent) : null)}
          loadingAgent={creatingAgent || updatingAgent}
          scrollToTop={!!createdAgent}
          onInvite={() => setAgent(
            (prev: Agent) => ({ ...prev, status: AgentStatus.INVITATION_SENT } as Agent)
          )}
          onCreate={(params: { sendInviteAfterCreate?: boolean } = {}) => createAgent({ ...agent, ...params })}
          onUpdate={(agentUpdated) =>
            agent?.id
              ? updateAgent({
                  id: agent?.id,
                  agent: agentUpdated || agent,
                })
              : null
          }
          sequence={sequence}
          responseError={responseError}
        />
      );
    },
  };

  const quotesPanel: PanelProps = {
    key: "quotes",
    className: classes.uiTab,
    menuItem: translate("quotes"),
    render: () => <Quote viewOnly={viewOnly} agentId={agent?.id} />,
  };

  const ordersPanel: PanelProps = {
    key: "orders",
    className: classes.uiTab,
    menuItem: translate("orders"),
    render: () => <Order viewOnly={viewOnly} agentId={agent?.id} />,
  };

  const pricingPanel: PanelProps = {
    key: "pricing",
    className: classes.uiTab,
    menuItem: translate("pricing"),
    render: () => (
      <Pricing
        isUpdating={creatingAgent || updatingAgent}
        viewOnly={viewOnly}
        onCancel={goBack}
        onSave={() => {
          if (agent) {
            if (agent?.id) updateAgent({ id: agent.id, agent });
            else createAgent(agent);
          }
        }}
      />
    ),
  };

  const getLeftPanels = () => {
    if (!agentId) return [detailsPanel];

    if (currentUser?.company?.profile?.initials === CompanyProfile.MV) {
      if (!agentPermission.edit) return [];
      return [detailsPanel, quotesPanel, ordersPanel, pricingPanel];
    }

    return [detailsPanel, quotesPanel, ordersPanel];
  };

  const agentName = useMemo(() => {
    return agent?.entity ?? "";
  }, [agent?.entity]);

  const agentStatus = useMemo(() => {
    return agent?.status ?? "";
  }, [agent?.status]);

  return (
    <div>
      {agentName && (
        <SubHeader>
          <Breadcrumb
            items={[
              {
                title: agentName,
                color: "#465869",
              },
            ]}
          />

          <BadgeStatus value={agentStatus} />
        </SubHeader>
      )}

      <div className={classes.mainDiv}>
        <UITabMaterial
          useInitialPadding
          leftPanels={getLeftPanels()}
          leftDataCy="left-panels"
          showRightPanel={leftPanelSelected === TAB_SELECTED.DETAILS}
          onChangeLeftPanel={(event, panel, key) => key && setLeftPanelSelected(key)}
        />
      </div>
    </div>
  );
};

const AgentCreateOrEditPage = () => {
  return (
    <AgentProvider>
      <AgentCreateOrEditPageContent />
    </AgentProvider>
  );
};

export { AgentCreateOrEditPage };
